import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { MarkdownContent } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import { scmAuthApiRef } from '@backstage/integration-react';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';

const MIN_QUERY_LENGTH = 2;
const ERROR_MIN_DISPLAY_MS = 1500;

type RepoState = {
  host: string;
  owner: string;
  repoName: string;
};

type RepoOption = {
  id: number;
  name: string;
  fullName: string;
  description?: string;
};

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  (error as { name: string }).name === 'AbortError';

const parseRepoPickerUrl = (value?: string): RepoState => {
  if (!value) {
    return { host: '', owner: '', repoName: '' };
  }

  try {
    const parsed = new URL(`https://${value}`);
    return {
      host: parsed.host,
      owner: parsed.searchParams.get('owner') ?? '',
      repoName: parsed.searchParams.get('repo') ?? '',
    };
  } catch (error) {
    return { host: '', owner: '', repoName: '' };
  }
};

const serializeRepoPickerUrl = (state: RepoState): string | undefined => {
  if (!state.host) {
    return undefined;
  }

  const params = new URLSearchParams();

  if (state.owner) {
    params.set('owner', state.owner);
  }

  if (state.repoName) {
    params.set('repo', state.repoName);
  }

  return `${state.host}?${params.toString()}`;
};

const buildGithubApiBaseUrl = (host: string) =>
  host === 'github.com' ? 'https://api.github.com' : `https://${host}/api/v3`;

export const GithubRepoFilter = ({
  onChange,
  rawErrors,
  required,
  formData,
  disabled,
  uiSchema,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const uiOptions = (uiSchema?.['ui:options'] as Record<string, unknown>) ?? {};
  const allowedHosts = (uiOptions.allowedHosts as string[]) ?? ['github.com'];
  const configuredAllowedOwners = (uiOptions.allowedOwners as string[]) ?? [];
  const hideHost = Boolean(uiOptions.hideHost);
  const hideOwner = Boolean(uiOptions.hideOwner);
  const requestUserCredentials = uiOptions.requestUserCredentials as
    | {
        secretsKey: string;
        additionalScopes?: Record<string, string[]>;
      }
    | undefined;

  const browserHostname = useMemo(
    () => (typeof window !== 'undefined' ? window.location.hostname : ''),
    [],
  );

  const environmentPreferredOwner = useMemo(() => {
    if (!hideOwner || configuredAllowedOwners.length > 0) {
      return undefined;
    }

    const host = browserHostname.toLowerCase();

    if (/dv|dev/.test(host) || host.includes('localhost') || host.includes('127.0.0.1')) {
      return 'nestle-devtest';
    }

    if (host.includes('idp.nestle.com') || host.includes('pp') || host.includes('prod')) {
      return 'nestle-it';
    }

    return undefined;
  }, [hideOwner, configuredAllowedOwners, browserHostname]);

  const allowedOwners = useMemo(() => {
    if (configuredAllowedOwners.length > 0) {
      return configuredAllowedOwners;
    }

    if (!hideOwner) {
      return [];
    }

    if (environmentPreferredOwner) {
      return [environmentPreferredOwner];
    }

    return [];
  }, [configuredAllowedOwners, hideOwner, environmentPreferredOwner]);

  const scmAuthApi = useApi(scmAuthApiRef);
  const { secrets, setSecrets } = useTemplateSecrets();

  const [state, setState] = useState<RepoState>(() =>
    parseRepoPickerUrl(typeof formData === 'string' ? formData : undefined),
  );
  const [repoOptions, setRepoOptions] = useState<RepoOption[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string>();
  const [credentialError, setCredentialError] = useState<string>();
  const [inputValue, setInputValue] = useState(state.repoName ?? '');
  const [searchTerm, setSearchTerm] = useState(state.repoName ?? '');
  const [selectedRepo, setSelectedRepo] = useState<RepoOption | null>(null);
  const [credentialsHost, setCredentialsHost] = useState<string>();
  const [isRepoValid, setIsRepoValid] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const [isValidatingRepo, setIsValidatingRepo] = useState(false);
  const [validatedRepoKey, setValidatedRepoKey] = useState<string>();
  const [lastFetchedQuery, setLastFetchedQuery] = useState<string>('');
  const [lastErrorKey, setLastErrorKey] = useState<string>('');

  const errorClearTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastErrorTimestampRef = useRef<number>(0);
  const activeFetchControllerRef = useRef<AbortController>();
  const lastFetchedSignatureRef = useRef<string>();
  const lastFailedSignatureRef = useRef<string>();

  const clearValidationState = useCallback((resetErrorKey?: boolean) => {
    setValidationError(undefined);
    if (resetErrorKey) {
      setLastErrorKey('');
    }
    lastErrorTimestampRef.current = 0;
  }, []);

  const requestClearValidationError = useCallback(
    (force?: boolean) => {
      if (!validationError) {
        return;
      }

      if (errorClearTimeoutRef.current) {
        clearTimeout(errorClearTimeoutRef.current);
        errorClearTimeoutRef.current = undefined;
      }

      if (force) {
        clearValidationState(true);
        return;
      }

      const elapsed = Date.now() - lastErrorTimestampRef.current;

      if (elapsed >= ERROR_MIN_DISPLAY_MS) {
        clearValidationState();
        return;
      }

      errorClearTimeoutRef.current = setTimeout(() => {
        errorClearTimeoutRef.current = undefined;
        clearValidationState();
      }, ERROR_MIN_DISPLAY_MS - elapsed);
    },
    [clearValidationState, validationError],
  );

  const showValidationError = useCallback(
    (message: string, options?: { errorKey?: string }) => {
      if (errorClearTimeoutRef.current) {
        clearTimeout(errorClearTimeoutRef.current);
        errorClearTimeoutRef.current = undefined;
      }

      lastErrorTimestampRef.current = Date.now();
      setValidationError(message);

      if (options?.errorKey !== undefined) {
        setLastErrorKey(options.errorKey);
      }
    },
    [],
  );

  const defaultHost = useMemo(
    () => allowedHosts[0] ?? 'github.com',
    [allowedHosts],
  );

  const baseApiUrl = useMemo(() => {
    if (!state.host) {
      return undefined;
    }
    return buildGithubApiBaseUrl(state.host);
  }, [state.host]);

  const accessToken = useMemo(() => {
    if (!requestUserCredentials) {
      return undefined;
    }
    return secrets[requestUserCredentials.secretsKey];
  }, [requestUserCredentials, secrets]);

  useEffect(() => {
    setState(prev => ({
      host: prev.host || defaultHost,
      owner: prev.owner,
      repoName: prev.repoName,
    }));
  }, [defaultHost]);

  useEffect(
    () => () => {
      if (errorClearTimeoutRef.current) {
        clearTimeout(errorClearTimeoutRef.current);
      }
      if (activeFetchControllerRef.current) {
        activeFetchControllerRef.current.abort();
      }
    },
    [],
  );

  useEffect(() => {
    const nextState = parseRepoPickerUrl(
      typeof formData === 'string' ? formData : undefined,
    );

    setState(prev => {
      if (
        prev.host === nextState.host &&
        prev.owner === nextState.owner &&
        prev.repoName === nextState.repoName
      ) {
        return prev;
      }

      return {
        host: nextState.host || prev.host,
        owner: nextState.owner,
        repoName: nextState.repoName,
      };
    });

    if (typeof formData === 'string' && formData.length > 0) {
      setInputValue(nextState.repoName ?? '');
      setSearchTerm('');
    }
  }, [formData]);

  useEffect(() => {
    const trimmed = inputValue.trim();

    if (state.repoName && trimmed === state.repoName) {
      return;
    }

    if (trimmed === searchTerm) {
      return;
    }

    const timeout = setTimeout(() => {
      setSearchTerm(trimmed);
    }, 400);

    return () => clearTimeout(timeout);
  }, [inputValue, searchTerm, state.repoName]);

  useEffect(() => {
    if (!state.owner && allowedOwners.length === 1) {
      setState(prev => ({ ...prev, owner: allowedOwners[0] }));
    }
  }, [allowedOwners, state.owner]);

  useEffect(() => {
    if (!state.host || !state.owner || !state.repoName || !isRepoValid) {
      onChange(undefined);
      return;
    }

    onChange(serializeRepoPickerUrl(state));
  }, [state, isRepoValid, onChange]);

  useEffect(() => {
    if (
      !requestUserCredentials ||
      !state.host ||
      (accessToken && credentialsHost === state.host)
    ) {
      return;
    }

    let cancelled = false;

    const requestToken = async () => {
      try {
        setCredentialError(undefined);
        const { token } = await scmAuthApi.getCredentials({
          url: `https://${state.host}`,
          additionalScope: {
            repoWrite: true,
            customScopes: requestUserCredentials.additionalScopes,
          },
        });

        if (!cancelled) {
          setSecrets({ [requestUserCredentials.secretsKey]: token });
          setCredentialsHost(state.host);
        }
      } catch (error) {
        if (!cancelled) {
          setCredentialError(
            (error as Error)?.message ?? 'Failed to request GitHub token',
          );
        }
      }
    };

    requestToken();

    return () => {
      cancelled = true;
    };
  }, [
    requestUserCredentials,
    state.host,
    accessToken,
    credentialsHost,
    scmAuthApi,
    setSecrets,
  ]);

  useEffect(() => {
    if (!state.host) {
      if (activeFetchControllerRef.current) {
        activeFetchControllerRef.current.abort();
        activeFetchControllerRef.current = undefined;
      }
      setRepoOptions([]);
      setFetchError(undefined);
      setIsFetching(false);
      setLastFetchedQuery('');
      lastFetchedSignatureRef.current = undefined;
      return;
    }

    if (hideOwner && allowedOwners.length === 0) {
      return;
    }

    const trimmedSearch = searchTerm.trim();

    if (trimmedSearch.length < MIN_QUERY_LENGTH) {
      if (activeFetchControllerRef.current) {
        activeFetchControllerRef.current.abort();
        activeFetchControllerRef.current = undefined;
      }
      setRepoOptions([]);
      setFetchError(undefined);
      setIsFetching(false);
      setLastFetchedQuery('');
      lastFetchedSignatureRef.current = undefined;
      lastFailedSignatureRef.current = undefined;
      return;
    }

    if (requestUserCredentials && !accessToken) {
      return;
    }

    const signature = `${state.host ?? ''}|${state.owner ?? ''}|${trimmedSearch.toLowerCase()}`;

    if (
      signature === lastFetchedSignatureRef.current ||
      signature === lastFailedSignatureRef.current
    ) {
      return;
    }

    if (activeFetchControllerRef.current) {
      activeFetchControllerRef.current.abort();
      activeFetchControllerRef.current = undefined;
    }

    const controller = new AbortController();
    activeFetchControllerRef.current = controller;
    let cancelled = false;

    const fetchRepos = async () => {
      try {
        setIsFetching(true);
        setFetchError(undefined);
        setLastFetchedQuery(trimmedSearch);

        const baseUrl = buildGithubApiBaseUrl(state.host);
        const search = new URLSearchParams();
        const ownerQualifier = state.owner
          ? `org:${state.owner}`
          : allowedOwners.length === 1
          ? `org:${allowedOwners[0]}`
          : '';
        const querySegments = [`${trimmedSearch} in:name`];

        if (ownerQualifier) {
          querySegments.push(ownerQualifier);
        }

        search.set('q', querySegments.join(' '));
        search.set('per_page', '20');

        const response = await fetch(`${baseUrl}/search/repositories?${search.toString()}`, {
          signal: controller.signal,
          headers: {
            Accept: 'application/vnd.github+json',
            ...(accessToken
              ? { Authorization: `token ${accessToken}` }
              : undefined),
          },
        });

        if (!response.ok) {
          const payload = await response
            .json()
            .catch(() => ({ message: `HTTP ${response.status}` }));
          throw new Error(payload?.message ?? `HTTP ${response.status}`);
        }

        const data = await response.json();
        const allowedOwnerSet = new Set(
          allowedOwners.map(owner => owner.toLowerCase()),
        );
        const items: RepoOption[] = Array.isArray(data?.items)
          ? data.items
              .filter((item: any) => {
                const itemOwner = item.owner?.login?.toLowerCase() ?? '';

                if (
                  allowedOwnerSet.size > 0 &&
                  !allowedOwnerSet.has(itemOwner)
                ) {
                  return false;
                }

                if (state.owner) {
                  return itemOwner === state.owner.toLowerCase();
                }

                return true;
              })
              .map((item: any) => ({
              id: item.id,
              name: item.name,
              fullName: item.full_name,
              description: item.description ?? undefined,
            }))
          : [];

        if (!cancelled) {
          setRepoOptions(items);
          lastFetchedSignatureRef.current = signature;
          lastFailedSignatureRef.current = items.length === 0 ? signature : undefined;
        }
      } catch (error) {
        if (cancelled || isAbortError(error)) {
          return;
        }

        setRepoOptions([]);
        setFetchError((error as Error)?.message ?? 'Failed to load repositories');
        lastFetchedSignatureRef.current = signature;
        lastFailedSignatureRef.current = signature;
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
        if (activeFetchControllerRef.current === controller) {
          activeFetchControllerRef.current = undefined;
        }
      }
    };

    fetchRepos();

    return () => {
      cancelled = true;
      controller.abort();
      if (activeFetchControllerRef.current === controller) {
        activeFetchControllerRef.current = undefined;
      }
    };
  }, [
    state.host,
    state.owner,
    searchTerm,
    accessToken,
    allowedOwners,
    requestUserCredentials,
    hideOwner,
  ]);

  useEffect(() => {
    if (!state.repoName) {
      setSelectedRepo(null);
      setIsRepoValid(false);
      requestClearValidationError(true);
      setValidatedRepoKey(undefined);
      return;
    }

    const match = repoOptions.find(option => option.name === state.repoName);

    if (match) {
      setSelectedRepo(match);
      setIsRepoValid(true);
      requestClearValidationError(true);
      setValidatedRepoKey(
        `${(state.owner ?? '').toLowerCase()}/${state.repoName.toLowerCase()}`,
      );
      setLastErrorKey('');
    }
  }, [repoOptions, state.repoName, state.owner, requestClearValidationError]);

  useEffect(() => {
    const trimmedSearch = searchTerm.trim();
    const trimmedSelection = state.repoName.trim();
    const validationTarget = trimmedSearch || trimmedSelection;
    const isDraft = trimmedSearch.length > 0;

    if (!validationTarget) {
      setIsValidatingRepo(false);
      setLastErrorKey('');
      return;
    }

    if (!isDraft && selectedRepo && selectedRepo.name === validationTarget) {
      setIsValidatingRepo(false);
      requestClearValidationError(true);
      return;
    }

    if (isDraft) {
      if (isFetching) {
        setIsValidatingRepo(true);
        requestClearValidationError();
        return;
      }

      if (lastFetchedQuery !== trimmedSearch) {
        setIsValidatingRepo(false);
        requestClearValidationError();
        return;
      }

      if (repoOptions.length > 0) {
        setIsRepoValid(false);
        requestClearValidationError();
        setIsValidatingRepo(false);
        setLastErrorKey('');
        return;
      }
    }

    if (validationTarget.length < MIN_QUERY_LENGTH) {
      setIsRepoValid(false);
      requestClearValidationError(true);
      setIsValidatingRepo(false);
      setLastErrorKey('');
      return;
    }

    if (!state.owner) {
      setIsRepoValid(false);
      showValidationError('Select an owner before continuing', { errorKey: '' });
      setValidatedRepoKey(undefined);
      setIsValidatingRepo(false);
      return;
    }

    if (requestUserCredentials && !accessToken) {
      setIsValidatingRepo(false);
      return;
    }

    const repoKey = `${state.owner}/${validationTarget}`.toLowerCase();

    if (!isDraft && repoKey === validatedRepoKey && isRepoValid) {
      setIsValidatingRepo(false);
      requestClearValidationError(true);
      return;
    }

    if (repoKey === lastErrorKey && validationError) {
      setIsValidatingRepo(false);
      return;
    }

    if (!baseApiUrl) {
      setIsValidatingRepo(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const validate = async () => {
      try {
        setIsValidatingRepo(true);
        requestClearValidationError();

        const response = await fetch(
          `${baseApiUrl}/repos/${state.owner}/${validationTarget}`,
          {
            signal: controller.signal,
            headers: {
              Accept: 'application/vnd.github+json',
              ...(accessToken
                ? { Authorization: `token ${accessToken}` }
                : undefined),
            },
          },
        );

        if (!response.ok) {
          const payload = await response
            .json()
            .catch(() => ({ message: `HTTP ${response.status}` }));
          throw new Error(payload?.message ?? `HTTP ${response.status}`);
        }

        if (!cancelled) {
          setIsRepoValid(true);
          requestClearValidationError(true);
          setValidatedRepoKey(repoKey);
          setLastErrorKey('');
        }
      } catch (error) {
        if (cancelled || isAbortError(error)) {
          return;
        }

        setIsRepoValid(false);
        setValidatedRepoKey(undefined);
        showValidationError(
          'Repository not found or not accessible in the selected organization',
          { errorKey: repoKey },
        );
        setLastErrorKey(repoKey);
      } finally {
        if (!cancelled) {
          setIsValidatingRepo(false);
        }
      }
    };

    validate();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    searchTerm,
    state.repoName,
    state.owner,
    selectedRepo,
    baseApiUrl,
    accessToken,
    requestUserCredentials,
    validatedRepoKey,
    isRepoValid,
    repoOptions,
    isFetching,
    lastFetchedQuery,
    lastErrorKey,
    requestClearValidationError,
    showValidationError,
    validationError,
  ]);

  const ownerItems = allowedOwners.map(owner => (
    <MenuItem key={owner} value={owner}>
      {owner}
    </MenuItem>
  ));

  return (
    <Box display="flex" flexDirection="column">
      {schema?.title ? (
        <Box mb={1}>
          <Typography variant="h6">{schema.title}</Typography>
          {schema?.description ? (
            <Typography variant="body2" color="textSecondary" component="div">
              <MarkdownContent content={schema.description} />
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {!hideHost ? (
        <FormControl margin="normal" disabled fullWidth>
          <TextField
            label="Host"
            value={state.host || defaultHost}
            disabled
          />
        </FormControl>
      ) : null}

      {!hideOwner ? (
        <FormControl
          margin="normal"
          required={required && allowedOwners.length > 0}
          error={Boolean(rawErrors?.length) && !state.owner}
          disabled={disabled}
          fullWidth
        >
          {allowedOwners.length ? (
            <>
              <InputLabel id="github-owner-label">Owner</InputLabel>
            <Select
              labelId="github-owner-label"
              value={state.owner}
              onChange={event => {
                const owner = String(event.target.value ?? '');
                setState(prev => ({ ...prev, owner, repoName: '' }));
                setInputValue('');
                setSelectedRepo(null);
                setIsRepoValid(false);
                requestClearValidationError(true);
                setValidatedRepoKey(undefined);
                setLastErrorKey('');
                setSearchTerm('');
                lastFetchedSignatureRef.current = undefined;
                setRepoOptions([]);
                setFetchError(undefined);
                setLastFetchedQuery('');
              }}
              displayEmpty={!state.owner}
              disabled={disabled || allowedOwners.length === 1}
            >
              {!state.owner ? (
                <MenuItem value="">
                  <em>Select an owner</em>
                </MenuItem>
              ) : null}
              {ownerItems}
            </Select>
            </>
          ) : (
            <TextField
              label="Owner"
              value={state.owner}
              onChange={event => {
                const owner = event.target.value ?? '';
                setState(prev => ({ ...prev, owner }));
                setIsRepoValid(false);
                requestClearValidationError(true);
                setValidatedRepoKey(undefined);
                setLastErrorKey('');
                setSearchTerm('');
                lastFetchedSignatureRef.current = undefined;
                setRepoOptions([]);
                setFetchError(undefined);
                setLastFetchedQuery('');
              }}
              placeholder="github organization"
              fullWidth
            />
          )}
          <FormHelperText>GitHub organization or user</FormHelperText>
        </FormControl>
      ) : null}

      <Autocomplete<RepoOption, false, false, true>
        disabled={disabled || !state.host}
        options={repoOptions}
        value={selectedRepo}
        getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
        onChange={(_, option) => {
          if (typeof option === 'string') {
            setSelectedRepo(null);
            setState(prev => ({ ...prev, repoName: '' }));
            setIsRepoValid(false);
            requestClearValidationError(true);
            setValidatedRepoKey(undefined);
            setLastErrorKey('');
            setInputValue(option);
            setSearchTerm(option);
            lastFetchedSignatureRef.current = undefined;
            return;
          }

          if (option) {
            setSelectedRepo(option);
            const optionOwner = (option.fullName?.split('/')?.[0] ?? '').trim();
            const resolvedOwner = optionOwner || state.owner;
            setState(prev => ({
              ...prev,
              owner: resolvedOwner || prev.owner,
              repoName: option.name,
            }));
            setInputValue(option.name);
            setIsRepoValid(true);
            requestClearValidationError(true);
            const ownerForKey = (resolvedOwner || '').toLowerCase();
            setValidatedRepoKey(
              ownerForKey ? `${ownerForKey}/${option.name.toLowerCase()}` : option.name.toLowerCase(),
            );
            setLastErrorKey('');
            setSearchTerm('');
            lastFetchedSignatureRef.current = undefined;
          } else {
            setSelectedRepo(null);
            setState(prev => ({ ...prev, repoName: '' }));
            setIsRepoValid(false);
            requestClearValidationError(true);
            setValidatedRepoKey(undefined);
            setLastErrorKey('');
            setSearchTerm('');
            lastFetchedSignatureRef.current = undefined;
          }
        }}
        inputValue={inputValue}
        onInputChange={(_, value, reason) => {
          setInputValue(value ?? '');

          if (reason === 'clear') {
            setSelectedRepo(null);
            setState(prev => ({ ...prev, repoName: '' }));
            setIsRepoValid(false);
            requestClearValidationError(true);
            setValidatedRepoKey(undefined);
            setLastErrorKey('');
            setSearchTerm('');
            lastFetchedSignatureRef.current = undefined;
            lastFailedSignatureRef.current = undefined;
            setRepoOptions([]);
            setFetchError(undefined);
            setLastFetchedQuery('');
            return;
          }

          setState(prev => ({
            ...prev,
            repoName: '',
            owner:
              prev.owner ||
              (allowedOwners.length === 1 ? allowedOwners[0] : prev.owner),
          }));

          if (!value) {
            setSelectedRepo(null);
            setIsRepoValid(false);
            requestClearValidationError(true);
            setValidatedRepoKey(undefined);
            setLastErrorKey('');
            setSearchTerm('');
            lastFetchedSignatureRef.current = undefined;
            lastFailedSignatureRef.current = undefined;
            setRepoOptions([]);
            setFetchError(undefined);
            setLastFetchedQuery('');
            return;
          }

          setIsRepoValid(false);
          requestClearValidationError();
          lastFetchedSignatureRef.current = undefined;
          lastFailedSignatureRef.current = undefined;
        }}
        filterOptions={options => options}
        renderInput={params => (
          <TextField
            {...params}
            label="Repository"
            required={required}
            margin="normal"
            error={
              Boolean(rawErrors?.length && !state.repoName) ||
              Boolean(validationError)
            }
            helperText={
              validationError
                ? validationError
                : rawErrors?.length && !state.repoName
                ? rawErrors.join(', ')
                : 'Search repositories by name'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching || isValidatingRepo ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            fullWidth
          />
        )}
        renderOption={option => {
          if (typeof option === 'string') {
            return option;
          }

          return (
            <Box display="flex" flexDirection="column">
              <Typography variant="body1">{option.fullName}</Typography>
              {option.description ? (
                <Typography variant="body2" color="textSecondary">
                  {option.description}
                </Typography>
              ) : null}
            </Box>
          );
        }}
      />

      {requestUserCredentials && !accessToken && !credentialError ? (
        <Box mt={1}>
          <Alert severity="info">
            Authorize GitHub to search private repositories.
          </Alert>
        </Box>
      ) : null}

      {fetchError ? (
        <Box mt={1}>
          <Alert severity="error">{fetchError}</Alert>
        </Box>
      ) : null}

      {credentialError ? (
        <Box mt={1}>
          <Alert severity="warning">{credentialError}</Alert>
        </Box>
      ) : null}
    </Box>
  );
};
